// This file is part of Indico.
// Copyright (C) 2002 - 2023 CERN
//
// Indico is free software; you can redistribute it and/or
// modify it under the terms of the MIT License; see the
// LICENSE file for more details.

import searchAffiliationURL from 'indico-url:users.api_affiliations';

import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {useForm, useFormState} from 'react-final-form';
import {Form, Message, Header, Button, Icon} from 'semantic-ui-react';

import {FinalComboDropdown, FinalDropdown, FinalInput, FinalTextArea} from 'indico/react/forms';
import {FinalModalForm} from 'indico/react/forms/final-form';
import {useDebouncedAsyncValidate} from 'indico/react/hooks';
import {indicoAxios, handleAxiosError} from 'indico/utils/axios';
import {camelizeKeys} from 'indico/utils/case';
import {makeAsyncDebounce} from 'indico/utils/debounce';

import {Param, Translate} from '../i18n';

import './PersonDetailsModal.module.scss';

const debounce = makeAsyncDebounce(250);

const titles = [
  {text: Translate.string('Dr'), value: 'dr'},
  {text: Translate.string('Prof'), value: 'prof'},
];

// TEMA: Split into title and Salutation and export this field for use in AdditionalParticipantsInput
export const salutations = [
  // TODO: {text: Translate.string('Dear Mr.'), value: 'mr'},
  // TODO: {text: Translate.string('Dear Ms.'), value: 'ms'},
  // TODO: {text: Translate.string('Dear'), value: 'mx'},
  {text: Translate.string('Mr'), value: 'mr'},
  {text: Translate.string('Ms'), value: 'ms'},
  {text: Translate.string('Mx'), value: 'mx'},
];

// TEMA: added country choice - list of countries
const countries = [
  {text: Translate.string('Afghanistan'), value: 'afghanistan'},
  {text: Translate.string('Åland Islands'), value: 'aland_islands'},
  {text: Translate.string('Albania'), value: 'albania'},
  {text: Translate.string('Algeria'), value: 'algeria'},
  {text: Translate.string('American Samoa'), value: 'american_samoa'},
  {text: Translate.string('Andorra'), value: 'andorra'},
  {text: Translate.string('Angola'), value: 'angola'},
  {text: Translate.string('Anguilla'), value: 'anguilla'},
  {text: Translate.string('Antarctica'), value: 'antarctica'},
  {
    text: Translate.string('Antigua and Barbuda'),
    value: 'antigua_and_barbuda',
  },
  {text: Translate.string('Argentina'), value: 'argentina'},
  {text: Translate.string('Armenia'), value: 'armenia'},
  {text: Translate.string('Aruba'), value: 'aruba'},
  {text: Translate.string('Australia'), value: 'australia'},
  {text: Translate.string('Austria'), value: 'austria'},
  {text: Translate.string('Azerbaijan'), value: 'azerbaijan'},
  {text: Translate.string('Bahamas'), value: 'bahamas'},
  {text: Translate.string('Bahrain'), value: 'bahrain'},
  {text: Translate.string('Bangladesh'), value: 'bangladesh'},
  {text: Translate.string('Barbados'), value: 'barbados'},
  {text: Translate.string('Belarus'), value: 'belarus'},
  {text: Translate.string('Belgium'), value: 'belgium'},
  {text: Translate.string('Belize'), value: 'belize'},
  {text: Translate.string('Benin'), value: 'benin'},
  {text: Translate.string('Bermuda'), value: 'bermuda'},
  {text: Translate.string('Bhutan'), value: 'bhutan'},
  {text: Translate.string('Bolivia'), value: 'bolivia'},
  {
    text: Translate.string('Bonaire, Sint Eustatius and Saba'),
    value: 'bonaire_sint_eustatius_and_saba',
  },
  {
    text: Translate.string('Bosnia and Herzegovina'),
    value: 'bosnia_and_herzegovina',
  },
  {text: Translate.string('Botswana'), value: 'botswana'},
  {text: Translate.string('Bouvet Island'), value: 'bouvet_island'},
  {text: Translate.string('Brazil'), value: 'brazil'},
  {
    text: Translate.string('British Indian Ocean Territory'),
    value: 'british_indian_ocean_territory',
  },
  {text: Translate.string('Brunei Darussalam'), value: 'brunei_darussalam'},
  {text: Translate.string('Bulgaria'), value: 'bulgaria'},
  {text: Translate.string('Burkina Faso'), value: 'burkina_faso'},
  {text: Translate.string('Burundi'), value: 'burundi'},
  {text: Translate.string('Cambodia'), value: 'cambodia'},
  {text: Translate.string('Cameroon'), value: 'cameroon'},
  {text: Translate.string('Canada'), value: 'canada'},
  {text: Translate.string('Cape Verde'), value: 'cape_verde'},
  {text: Translate.string('Cayman Islands'), value: 'cayman_islands'},
  {
    text: Translate.string('Central African Republic'),
    value: 'central_african_republic',
  },
  {text: Translate.string('Chad'), value: 'chad'},
  {text: Translate.string('Chile'), value: 'chile'},
  {text: Translate.string('China'), value: 'china'},
  {text: Translate.string('Christmas Island'), value: 'christmas_island'},
  {
    text: Translate.string('Cocos (Keeling) Islands'),
    value: 'cocos_keeling_islands',
  },
  {text: Translate.string('Colombia'), value: 'colombia'},
  {text: Translate.string('Comoros'), value: 'comoros'},
  {text: Translate.string('Congo'), value: 'congo'},
  {
    text: Translate.string('Congo, the Democratic Republic of the'),
    value: 'congo_the_democratic_republic_of_the',
  },
  {text: Translate.string('Cook Islands'), value: 'cook_islands'},
  {text: Translate.string('Costa Rica'), value: 'costa_rica'},
  {text: Translate.string("Côte d'Ivoire"), value: 'cote_d_ivoire'},
  {text: Translate.string('Croatia'), value: 'croatia'},
  {text: Translate.string('Cuba'), value: 'cuba'},
  {text: Translate.string('Curaçao'), value: 'curacao'},
  {text: Translate.string('Cyprus'), value: 'cyprus'},
  {text: Translate.string('Czech Republic'), value: 'czech_republic'},
  {text: Translate.string('Denmark'), value: 'denmark'},
  {text: Translate.string('Djibouti'), value: 'djibouti'},
  {text: Translate.string('Dominica'), value: 'dominica'},
  {text: Translate.string('Dominican Republic'), value: 'dominican_republic'},
  {text: Translate.string('Ecuador'), value: 'ecuador'},
  {text: Translate.string('Egypt'), value: 'egypt'},
  {text: Translate.string('El Salvador'), value: 'el_salvador'},
  {text: Translate.string('Equatorial Guinea'), value: 'equatorial_guinea'},
  {text: Translate.string('Eritrea'), value: 'eritrea'},
  {text: Translate.string('Estonia'), value: 'estonia'},
  {text: Translate.string('Ethiopia'), value: 'ethiopia'},
  {
    text: Translate.string('Falkland Islands (Malvinas)'),
    value: 'falkland_islands_malvinas',
  },
  {text: Translate.string('Faroe Islands'), value: 'faroe_islands'},
  {text: Translate.string('Fiji'), value: 'fiji'},
  {text: Translate.string('Finland'), value: 'finland'},
  {text: Translate.string('France'), value: 'france'},
  {text: Translate.string('French Guiana'), value: 'french_guiana'},
  {text: Translate.string('French Polynesia'), value: 'french_polynesia'},
  {
    text: Translate.string('French Southern Territories'),
    value: 'french_southern_territories',
  },
  {text: Translate.string('Gabon'), value: 'gabon'},
  {text: Translate.string('Gambia'), value: 'gambia'},
  {text: Translate.string('Georgia'), value: 'georgia'},
  {text: Translate.string('Germany'), value: 'germany'},
  {text: Translate.string('Ghana'), value: 'ghana'},
  {text: Translate.string('Gibraltar'), value: 'gibraltar'},
  {text: Translate.string('Greece'), value: 'greece'},
  {text: Translate.string('Greenland'), value: 'greenland'},
  {text: Translate.string('Grenada'), value: 'grenada'},
  {text: Translate.string('Guadeloupe'), value: 'guadeloupe'},
  {text: Translate.string('Guam'), value: 'guam'},
  {text: Translate.string('Guatemala'), value: 'guatemala'},
  {text: Translate.string('Guernsey'), value: 'guernsey'},
  {text: Translate.string('Guinea'), value: 'guinea'},
  {text: Translate.string('Guinea-Bissau'), value: 'guinea_bissau'},
  {text: Translate.string('Guyana'), value: 'guyana'},
  {text: Translate.string('Haiti'), value: 'haiti'},
  {
    text: Translate.string('Heard Island and McDonald Islands'),
    value: 'heard_island_and_mcdonald_islands',
  },
  {
    text: Translate.string('Holy See (Vatican City State)'),
    value: 'holy_see_vatican_city_state',
  },
  {text: Translate.string('Honduras'), value: 'honduras'},
  {text: Translate.string('Hong Kong'), value: 'hong_kong'},
  {text: Translate.string('Hungary'), value: 'hungary'},
  {text: Translate.string('Iceland'), value: 'iceland'},
  {text: Translate.string('India'), value: 'india'},
  {text: Translate.string('Indonesia'), value: 'indonesia'},
  {text: Translate.string('Iran'), value: 'iran'},
  {text: Translate.string('Iraq'), value: 'iraq'},
  {text: Translate.string('Ireland'), value: 'ireland'},
  {text: Translate.string('Isle of Man'), value: 'isle_of_man'},
  {text: Translate.string('Israel'), value: 'israel'},
  {text: Translate.string('Italy'), value: 'italy'},
  {text: Translate.string('Jamaica'), value: 'jamaica'},
  {text: Translate.string('Japan'), value: 'japan'},
  {text: Translate.string('Jersey'), value: 'jersey'},
  {text: Translate.string('Jordan'), value: 'jordan'},
  {text: Translate.string('Kazakhstan'), value: 'kazakhstan'},
  {text: Translate.string('Kenya'), value: 'kenya'},
  {text: Translate.string('Kiribati'), value: 'kiribati'},
  {
    text: Translate.string("Korea, Democratic People's Republic of"),
    value: 'korea_democratic_peoples_republic_of',
  },
  {text: Translate.string('Korea'), value: 'korea'},
  {text: Translate.string('Kuwait'), value: 'kuwait'},
  {text: Translate.string('Kyrgyzstan'), value: 'kyrgyzstan'},
  {text: Translate.string('Laos'), value: 'laos'},
  {text: Translate.string('Latvia'), value: 'latvia'},
  {text: Translate.string('Lebanon'), value: 'lebanon'},
  {text: Translate.string('Lesotho'), value: 'lesotho'},
  {text: Translate.string('Liberia'), value: 'liberia'},
  {text: Translate.string('Libya'), value: 'libya'},
  {text: Translate.string('Liechtenstein'), value: 'liechtenstein'},
  {text: Translate.string('Lithuania'), value: 'lithuania'},
  {text: Translate.string('Luxembourg'), value: 'luxembourg'},
  {text: Translate.string('Macao'), value: 'macao'},
  {text: Translate.string('Madagascar'), value: 'madagascar'},
  {text: Translate.string('Malawi'), value: 'malawi'},
  {text: Translate.string('Malaysia'), value: 'malaysia'},
  {text: Translate.string('Maldives'), value: 'maldives'},
  {text: Translate.string('Mali'), value: 'mali'},
  {text: Translate.string('Malta'), value: 'malta'},
  {text: Translate.string('Marshall Islands'), value: 'marshall_islands'},
  {text: Translate.string('Martinique'), value: 'martinique'},
  {text: Translate.string('Mauritania'), value: 'mauritania'},
  {text: Translate.string('Mauritius'), value: 'mauritius'},
  {text: Translate.string('Mayotte'), value: 'mayotte'},
  {text: Translate.string('Mexico'), value: 'mexico'},
  {text: Translate.string('Micronesia'), value: 'micronesia'},
  {text: Translate.string('Moldova'), value: 'moldova'},
  {text: Translate.string('Monaco'), value: 'monaco'},
  {text: Translate.string('Mongolia'), value: 'mongolia'},
  {text: Translate.string('Montenegro'), value: 'montenegro'},
  {text: Translate.string('Montserrat'), value: 'montserrat'},
  {text: Translate.string('Morocco'), value: 'morocco'},
  {text: Translate.string('Mozambique'), value: 'mozambique'},
  {text: Translate.string('Myanmar'), value: 'myanmar'},
  {text: Translate.string('Namibia'), value: 'namibia'},
  {text: Translate.string('Nauru'), value: 'nauru'},
  {text: Translate.string('Nepal'), value: 'nepal'},
  {text: Translate.string('Netherlands'), value: 'netherlands'},
  {text: Translate.string('New Caledonia'), value: 'new_caledonia'},
  {text: Translate.string('New Zealand'), value: 'new_zealand'},
  {text: Translate.string('Nicaragua'), value: 'nicaragua'},
  {text: Translate.string('Niger'), value: 'niger'},
  {text: Translate.string('Nigeria'), value: 'nigeria'},
  {text: Translate.string('Niue'), value: 'niue'},
  {text: Translate.string('Norfolk Island'), value: 'norfolk_island'},
  {text: Translate.string('North Macedonia'), value: 'north_macedonia'},
  {
    text: Translate.string('Northern Mariana Islands'),
    value: 'northern_mariana_islands',
  },
  {text: Translate.string('Norway'), value: 'norway'},
  {text: Translate.string('Oman'), value: 'oman'},
  {text: Translate.string('Pakistan'), value: 'pakistan'},
  {text: Translate.string('Palau'), value: 'palau'},
  {text: Translate.string('Palestine'), value: 'palestine'},
  {text: Translate.string('Panama'), value: 'panama'},
  {text: Translate.string('Papua New Guinea'), value: 'papua_new_guinea'},
  {text: Translate.string('Paraguay'), value: 'paraguay'},
  {text: Translate.string('Peru'), value: 'peru'},
  {text: Translate.string('Philippines'), value: 'philippines'},
  {text: Translate.string('Pitcairn'), value: 'pitcairn'},
  {text: Translate.string('Poland'), value: 'poland'},
  {text: Translate.string('Portugal'), value: 'portugal'},
  {text: Translate.string('Puerto Rico'), value: 'puerto_rico'},
  {text: Translate.string('Qatar'), value: 'qatar'},
  {text: Translate.string('Réunion'), value: 'reunion'},
  {text: Translate.string('Romania'), value: 'romania'},
  {text: Translate.string('Russia'), value: 'russia'},
  {text: Translate.string('Rwanda'), value: 'rwanda'},
  {text: Translate.string('Saint Barthélemy'), value: 'saint_barthelemy'},
  {
    text: Translate.string('Saint Helena, Ascension and Tristan da Cunha'),
    value: 'saint_helena_ascension_and_tristan_da_cunha',
  },
  {
    text: Translate.string('Saint Kitts and Nevis'),
    value: 'saint_kitts_and_nevis',
  },
  {text: Translate.string('Saint Lucia'), value: 'saint_lucia'},
  {
    text: Translate.string('Saint Martin (French part)'),
    value: 'saint_martin_french_part',
  },
  {
    text: Translate.string('Saint Pierre and Miquelon'),
    value: 'saint_pierre_and_miquelon',
  },
  {
    text: Translate.string('Saint Vincent and the Grenadines'),
    value: 'saint_vincent_and_the_grenadines',
  },
  {text: Translate.string('Samoa'), value: 'samoa'},
  {text: Translate.string('San Marino'), value: 'san_marino'},
  {
    text: Translate.string('Sao Tome and Principe'),
    value: 'sao_tome_and_principe',
  },
  {text: Translate.string('Saudi Arabia'), value: 'saudi_arabia'},
  {text: Translate.string('Senegal'), value: 'senegal'},
  {text: Translate.string('Serbia'), value: 'serbia'},
  {text: Translate.string('Seychelles'), value: 'seychelles'},
  {text: Translate.string('Sierra Leone'), value: 'sierra_leone'},
  {text: Translate.string('Singapore'), value: 'singapore'},
  {
    text: Translate.string('Sint Maarten (Dutch part)'),
    value: 'sint_maarten_dutch_part_',
  },
  {text: Translate.string('Slovakia'), value: 'slovakia'},
  {text: Translate.string('Slovenia'), value: 'slovenia'},
  {text: Translate.string('Solomon Islands'), value: 'solomon_islands'},
  {text: Translate.string('Somalia'), value: 'somalia'},
  {text: Translate.string('South Africa'), value: 'south_africa'},
  {
    text: Translate.string('South Georgia and the South Sandwich Islands'),
    value: 'south_georgia_and_the_south_sandwich_islands',
  },
  {text: Translate.string('South Sudan'), value: 'south_sudan'},
  {text: Translate.string('Spain'), value: 'spain'},
  {text: Translate.string('Sri Lanka'), value: 'sri_lanka'},
  {text: Translate.string('Sudan'), value: 'sudan'},
  {text: Translate.string('Suriname'), value: 'suriname'},
  {
    text: Translate.string('Svalbard and Jan Mayen'),
    value: 'svalbard_and_jan_mayen',
  },
  {text: Translate.string('Swaziland'), value: 'swaziland'},
  {text: Translate.string('Sweden'), value: 'sweden'},
  {text: Translate.string('Switzerland'), value: 'switzerland'},
  {text: Translate.string('Syria'), value: 'syria'},
  {text: Translate.string('Taiwan'), value: 'taiwan'},
  {text: Translate.string('Tajikistan'), value: 'tajikistan'},
  {text: Translate.string('Tanzania'), value: 'tanzania'},
  {text: Translate.string('Thailand'), value: 'thailand'},
  {text: Translate.string('Timor-Leste'), value: 'timor_leste'},
  {text: Translate.string('Togo'), value: 'togo'},
  {text: Translate.string('Tokelau'), value: 'tokelau'},
  {text: Translate.string('Tonga'), value: 'tonga'},
  {
    text: Translate.string('Trinidad and Tobago'),
    value: 'trinidad_and_tobago',
  },
  {text: Translate.string('Tunisia'), value: 'tunisia'},
  {text: Translate.string('Turkey'), value: 'turkey'},
  {text: Translate.string('Turkmenistan'), value: 'turkmenistan'},
  {
    text: Translate.string('Turks and Caicos Islands'),
    value: 'turks_and_caicos_islands',
  },
  {text: Translate.string('Tuvalu'), value: 'tuvalu'},
  {text: Translate.string('Uganda'), value: 'uganda'},
  {text: Translate.string('Ukraine'), value: 'ukraine'},
  {
    text: Translate.string('United Arab Emirates'),
    value: 'united_arab_emirates',
  },
  {text: Translate.string('United Kingdom'), value: 'united_kingdom'},
  {text: Translate.string('USA'), value: 'usa'},
  {
    text: Translate.string('United States Minor Outlying Islands'),
    value: 'united_states_minor_outlying_islands',
  },
  {text: Translate.string('Uruguay'), value: 'uruguay'},
  {text: Translate.string('Uzbekistan'), value: 'uzbekistan'},
  {text: Translate.string('Vanuatu'), value: 'vanuatu'},
  {text: Translate.string('Venezuela'), value: 'venezuela'},
  {text: Translate.string('Viet Nam'), value: 'viet_nam'},
  {
    text: Translate.string('Virgin Islands, British'),
    value: 'virgin_islands_british',
  },
  {
    text: Translate.string('Virgin Islands, U.S.'),
    value: 'virgin_islands_u_s',
  },
  {text: Translate.string('Wallis and Futuna'), value: 'wallis_and_futuna'},
  {text: Translate.string('Western Sahara'), value: 'western_sahara'},
  {text: Translate.string('Yemen'), value: 'yemen'},
  {text: Translate.string('Zambia'), value: 'zambia'},
  {text: Translate.string('Zimbabwe'), value: 'zimbabwe'},
];

// TEMA: Export this field for use in AdditionalParticipantsInput
export const FinalAffiliationField = ({hasPredefinedAffiliations}) => {
  const formState = useFormState();
  const currentAffiliation = formState.values.affiliationMeta;
  const [_affiliationResults, setAffiliationResults] = useState([]);
  const affiliationResults =
    currentAffiliation && !_affiliationResults.find(x => x.id === currentAffiliation.id)
      ? [currentAffiliation, ..._affiliationResults]
      : _affiliationResults;

  const getSubheader = ({city, countryName}) => {
    if (city && countryName) {
      return `${city}, ${countryName}`;
    }
    return city || countryName;
  };

  const affiliationOptions = affiliationResults.map(res => ({
    key: res.id,
    value: res.id,
    meta: res,
    text: `${res.name} `, // XXX: the space allows addition even if the entered text matches a result item
    content: <Header style={{fontSize: 14}} content={res.name} subheader={getSubheader(res)} />,
  }));

  const searchAffiliationChange = async (evt, {searchQuery}) => {
    if (!searchQuery) {
      setAffiliationResults([]);
      return;
    }
    let resp;
    try {
      resp = await debounce(() => indicoAxios.get(searchAffiliationURL({q: searchQuery})));
    } catch (error) {
      handleAxiosError(error);
      return;
    }
    setAffiliationResults(camelizeKeys(resp.data));
  };

  return hasPredefinedAffiliations ? (
    <FinalComboDropdown
      name="affiliationData"
      options={affiliationOptions}
      fluid
      includeMeta
      additionLabel={Translate.string('Use custom affiliation:') + ' '} // eslint-disable-line prefer-template
      onSearchChange={searchAffiliationChange}
      placeholder={Translate.string('Select an affiliation or add your own')}
      noResultsMessage={Translate.string('Search an affiliation or enter one manually')}
      renderCustomOptionContent={value => (
        <Header content={value} subheader={Translate.string('You entered this option manually')} />
      )}
    />
  ) : (
    <FinalInput name="affiliation" />
  );
};

FinalAffiliationField.propTypes = {
  hasPredefinedAffiliations: PropTypes.bool.isRequired,
};

export default function PersonDetailsModal({
  hasPredefinedAffiliations,
  onSubmit,
  onClose,
  person,
  otherPersons,
  hideEmailField,
  validateEmailUrl,
}) {
  return (
    <FinalModalForm
      id="person-link-details"
      size="tiny"
      onClose={onClose}
      onSubmit={onSubmit}
      header={person ? Translate.string('Edit Person') : Translate.string('Enter Person')}
      submitLabel={Translate.string('Save')}
      initialValues={
        person
          ? {...person, affiliationData: {id: person.affiliationId, text: person.affiliation}}
          : {affiliationData: {id: null, text: ''}, email: ''}
      }
    >
      {/* eslint-disable-next-line eqeqeq */}
      {person && person.userId != null && (
        <Translate as={Message}>
          You are updating details that were originally linked to a user. Please note that its
          identity will remain the same.
        </Translate>
      )}
      <Form.Group widths="equal">
        <Form.Field>
          {/* TEMA: Added Salutation */}
          <Translate as="label">Salutation</Translate>
          <FinalDropdown name="salutation" fluid search selection options={salutations} required />
        </Form.Field>
        <Form.Field>
          <Translate as="label">Academic Title</Translate>
          <FinalDropdown
            name="title"
            fluid
            search
            selection
            options={titles}
            placeholder={Translate.string('None')}
          />
        </Form.Field>
      </Form.Group>
      <Form.Field>
        <Translate as="label">Affiliation</Translate>
        <FinalAffiliationField hasPredefinedAffiliations={hasPredefinedAffiliations} required />
      </Form.Field>
      <Form.Group widths="equal">
        <Form.Field>
          <Translate as="label">First Name</Translate>
          <FinalInput name="firstName" required />
        </Form.Field>
        <Form.Field>
          <Translate as="label">Family Name</Translate>
          <FinalInput name="lastName" required />
        </Form.Field>
      </Form.Group>
      {!hideEmailField && (
        <Form.Field>
          <Translate as="label">Email</Translate>
          <EmailField
            validateUrl={validateEmailUrl}
            person={person}
            otherPersons={otherPersons}
            required
          />
        </Form.Field>
      )}
      <Form.Field>
        <Translate as="label">Country</Translate>
        <FinalDropdown name="country" fluid search selection options={countries} required />
      </Form.Field>
      <Form.Group widths="equal">
        <Form.Field>
          <Translate as="label">Address</Translate>
          <FinalTextArea name="address" />
        </Form.Field>
        <Form.Field>
          <Translate as="label">Telephone</Translate>
          <FinalInput name="phone" />
        </Form.Field>
      </Form.Group>
    </FinalModalForm>
  );
}

PersonDetailsModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  person: PropTypes.object,
  otherPersons: PropTypes.array,
  hasPredefinedAffiliations: PropTypes.bool.isRequired,
  hideEmailField: PropTypes.bool,
  validateEmailUrl: PropTypes.string,
};

PersonDetailsModal.defaultProps = {
  person: undefined,
  otherPersons: [],
  hideEmailField: false,
  validateEmailUrl: null,
};

function EmailField({validateUrl, person, otherPersons}) {
  let response, msg;
  const [message, setMessage] = useState({status: '', message: ''});
  const form = useForm();
  const [user, setUser] = useState(null);
  const [eventPerson, setEventPerson] = useState(null);
  const isUpdate = !!person;

  const validateEmail = useDebouncedAsyncValidate(async email => {
    form.change('personId', person?.personId);
    form.change('avatarURL', null);
    email = email.trim();

    if (email === '' || email === person?.email) {
      setMessage({status: '', message: ''});
      return;
    }

    // Prevent adding the same person twice
    if (otherPersons.some(p => p.email === email)) {
      msg = Translate.string('There is already a person with this email in the list.');
      setMessage({
        status: 'error',
        message: msg,
      });
      return msg;
    }

    setMessage({
      status: '',
      message: Translate.string('Checking email address...'),
    });

    try {
      response = await indicoAxios.get(validateUrl, {
        params: {email},
      });
    } catch (error) {
      return handleAxiosError(error);
    }

    const data = camelizeKeys(response.data);
    const {status, conflict} = data;
    setUser(data.user);
    setEventPerson(data.eventPerson);

    // A user can have multiple emails associated with their account.
    // Check already added persons if there is any with the same userId
    if (data.user) {
      const existingPerson = otherPersons.find(p => data.user.id === p.userId);
      if (existingPerson) {
        msg = (
          <Translate>
            This email is associated with{' '}
            <Param name="name" wrapper={<strong />} value={existingPerson.name} /> who is already in
            the list.
          </Translate>
        );
        setMessage({
          status: 'error',
          message: msg,
        });
        return msg;
      }
    }

    if (conflict === 'person-already-exists' || conflict === 'user-and-person-already-exists') {
      form.change('personId', data.eventPerson.id);
    }

    // Load the user's avatar
    if (conflict === 'user-already-exists' || conflict === 'user-and-person-already-exists') {
      form.change('avatarURL', data.user.avatarURL);
    }

    if (
      conflict === 'user-already-exists' ||
      conflict === 'person-already-exists' ||
      conflict === 'user-and-person-already-exists'
    ) {
      const obj = data.eventPerson || data.user;
      const name = obj.fullName || obj.name;
      if (isUpdate) {
        msg = (
          <Translate>
            This email is already used by <Param name="name" wrapper={<strong />} value={name} />.
            You can update the form with their information.
          </Translate>
        );
      } else {
        msg = (
          <Translate>
            This email is already used by <Param name="name" wrapper={<strong />} value={name} />.
            You can add the person directly or update the form with their information.
          </Translate>
        );
      }
    } else if (conflict === 'email-invalid') {
      if (data.emailError === 'undeliverable') {
        msg = Translate.string('The domain used in the email address does not exist.');
      } else {
        msg = Translate.string('This email address is invalid.');
      }
    } else if (status === 'ok') {
      msg = Translate.string('This email is currently not linked to this event.');
    }

    setMessage({status, message: msg});
    if (status === 'error') {
      return msg;
    }
  }, 250);

  const onClick = async () => {
    if (user) {
      form.change('userId', user.id);
    }
    const obj = eventPerson || user;
    form.change('address', obj.address);
    form.change('name', obj.fullName || obj.name);
    form.change('firstName', obj.firstName);
    form.change('lastName', obj.lastName);
    form.change('title', obj.title);
    form.change('phone', obj.phone);
    form.change('affiliationData', {id: obj.affiliationId, text: obj.affiliation});
    form.change('affiliationMeta', obj.affiliationMeta);
  };

  const emailBtns = (
    <div styleName="email-buttons">
      {!isUpdate && (
        <Button icon type="submit" primary size="tiny" labelPosition="right" onClick={onClick}>
          <Icon name="add" />
          <Translate>Add</Translate>
        </Button>
      )}
      <Button icon type="button" size="tiny" labelPosition="right" onClick={onClick}>
        <Icon name="sync" />
        <Translate>Update</Translate>
      </Button>
    </div>
  );

  const showEmailBtns = message.status !== 'error' && (!!user || !!eventPerson);

  return (
    <FinalInput
      type="email"
      name="email"
      validate={validateUrl ? validateEmail : undefined}
      // hide the normal error tooltip if we have an error from our async validation
      hideValidationError={message.status === 'error' ? 'message' : false}
      loaderWhileValidating
    >
      {!!message.message && (
        <Message
          visible
          error={message.status === 'error'}
          warning={message.status === 'warning'}
          positive={message.status === 'ok'}
        >
          <div>{message.message}</div>
          {showEmailBtns && emailBtns}
        </Message>
      )}
    </FinalInput>
  );
}

EmailField.propTypes = {
  validateUrl: PropTypes.string,
  person: PropTypes.object,
  otherPersons: PropTypes.array.isRequired,
};

EmailField.defaultProps = {
  person: null,
  validateUrl: null,
};
